import { createBrowserRouter, type RouteObject } from 'react-router';
import { AppLayout } from '../components/AppLayout';
import { AuthLayout } from '../components/AuthLayout';
import { NotFound } from '../components/NotFound';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
import { RouteFallback } from '../components/RouteFallback';
import { redirectIfSignedIn, sessionLoader } from '../session';
import {
  DASHBOARD_ROUTE,
  PROJECT_ROUTE,
  PROJECTS_LIST_ROUTE,
  REPORT_ROUTE,
  ROUTES,
  type RouteHandle,
} from './routes';

/**
 * The route tree, exported separately from the router.
 *
 * `router.test.tsx` mounts this exact array under `createMemoryRouter`. A test
 * that declares its own copy of the routes passes while the real tree is
 * broken, which makes it worse than no test.
 *
 * Route modules are lazy so the initial bundle carries only the shell —
 * NFR-2 allows 3 s to interactive on the workspace, and that budget is spent
 * on MapLibre, not on routes the user has not asked for.
 */
export const routes: RouteObject[] = [
  {
    path: ROUTES.root,
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    // Everything inside is behind sign-in (P0-WEB-07). On the parent rather
    // than repeated per route: a guard that has to be remembered is one that
    // eventually is not, and the failure mode is a page that renders without a
    // session rather than anything that looks wrong.
    id: 'session',
    loader: sessionLoader,
    // Loaders are the portal's data layer, so the first paint of a cold load
    // happens before any of them resolve. Without this the router renders
    // nothing at all until they do, which reads as a broken deploy on a slow
    // connection.
    HydrateFallback: RouteFallback,
    children: [
      {
        index: true,
        lazy: async () => {
          const [{ ProjectsRoute }, { loader }] = await Promise.all([
            import('../routes/projects/projects'),
            import('../routes/projects/projects.data'),
          ]);
          return { Component: ProjectsRoute, loader };
        },
      },
      {
        path: ROUTES.tools,
        handle: { fullscreen: true } satisfies RouteHandle,
        lazy: async () => {
          const [{ ToolsRoute }, { loadGoogleBasemaps }] = await Promise.all([
            import('../routes/tools/tools'),
            import('../components/map/basemaps'),
          ]);
          // So the canvas opens on Google's map rather than switching to it.
          return { Component: ToolsRoute, loader: () => loadGoogleBasemaps().then(() => null) };
        },
      },
      {
        path: PROJECTS_LIST_ROUTE,
        lazy: async () => {
          const [{ ProjectListRoute }, { loader }] = await Promise.all([
            import('../routes/projects/list'),
            import('../routes/projects/list.data'),
          ]);
          return { Component: ProjectListRoute, loader };
        },
      },
      {
        path: PROJECT_ROUTE,
        lazy: async () => {
          const [{ WorkspaceRoute }, { loader }] = await Promise.all([
            import('../routes/workspace/workspace'),
            import('../routes/workspace/workspace.data'),
          ]);
          return { Component: WorkspaceRoute, loader };
        },
      },
      {
        path: REPORT_ROUTE,
        lazy: async () => {
          const [{ ReportRoute }, { loader }] = await Promise.all([
            import('../routes/report/report'),
            import('../routes/report/report.data'),
          ]);
          return { Component: ReportRoute, loader };
        },
      },
      {
        path: DASHBOARD_ROUTE,
        lazy: async () => {
          const [{ DashboardRoute }, { loader }] = await Promise.all([
            import('../routes/dashboard/dashboard'),
            import('../routes/workspace/workspace.data'),
          ]);
          return { Component: DashboardRoute, loader };
        },
      },
      {
        path: ROUTES.accountSessions,
        lazy: async () => {
          const [{ SessionsRoute }, { loader }] = await Promise.all([
            import('../routes/account/sessions'),
            import('../routes/account/sessions.data'),
          ]);
          return { Component: SessionsRoute, loader };
        },
      },
      // Catch-all. Renders the same component the error boundary uses for a
      // thrown 404, so an unknown URL and a missing resource look alike.
      //
      // It does not swallow the auth routes below: React Router ranks routes by
      // specificity rather than by order, so a static path always beats a
      // splat. `router.test.tsx` pins that, because it is a property of the
      // library rather than of anything visible in this file.
      { path: '*', element: <NotFound /> },
    ],
  },

  // The signed-out screens (P0-WEB-06), under their own shell. `AppLayout`
  // opens the Reverb socket, and a visitor who has not signed in has no
  // organisation to subscribe to and no session to authorise a channel with.
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: RouteFallback,
    children: [
      {
        path: ROUTES.signIn,
        // The inverse guard. Answered from memory, so an anonymous visitor
        // pays nothing for it — see `redirectIfSignedIn`.
        loader: redirectIfSignedIn,
        lazy: async () => ({ Component: (await import('../routes/auth/sign-in')).SignInRoute }),
      },
      {
        path: ROUTES.signUp,
        lazy: async () => ({ Component: (await import('../routes/auth/sign-up')).SignUpRoute }),
      },
      {
        path: ROUTES.forgotPassword,
        lazy: async () => ({
          Component: (await import('../routes/auth/forgot-password')).ForgotPasswordRoute,
        }),
      },
      {
        path: ROUTES.resetPassword,
        lazy: async () => ({
          Component: (await import('../routes/auth/reset-password')).ResetPasswordRoute,
        }),
      },
      {
        path: ROUTES.verifyEmail,
        lazy: async () => ({
          Component: (await import('../routes/auth/verify-email')).VerifyEmailRoute,
        }),
      },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
